// SEO service - generates metadata + injects JSON-LD into document head.
// Centralizing this makes it easy to swap to a static-site / Next.js port later.

const SITE_NAME = "ZaranTech";
const DEFAULT_URL = "https://www.zarantech.com";
const DEFAULT_OG = "/og-default.png";

// Primary markets the site targets, as ISO 3166-1 alpha-2 codes.
// GB = United Kingdom (UK), IN = India. Emitted as one geo.region meta each.
export const GEO_REGIONS = ["US", "GB", "IN"];

export function buildMeta({
  title,
  description,
  path = "/",
  ogImage = DEFAULT_OG,
}) {
  // If a page/admin supplies a title that already carries the brand, use it
  // verbatim; otherwise append the standard " | ZaranTech" suffix.
  const fullTitle = title
    ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
    : `${SITE_NAME} | Global IT Training`;
  const url = `${DEFAULT_URL}${path}`;
  // Social crawlers expect an absolute image URL; resolve a relative one
  // (including the default) against the site origin.
  const usingDefaultImage = !ogImage || ogImage === DEFAULT_OG;
  const absoluteOgImage = /^https?:\/\//i.test(ogImage) ? ogImage : `${DEFAULT_URL}${ogImage}`;
  return { fullTitle, description, url, ogImage: absoluteOgImage, usingDefaultImage };
}

function ensureMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === "tag") return;
    if (v == null) return;
    el.setAttribute(k, v);
  });
  return el;
}

function removeMeta(selector) {
  if (typeof document === "undefined") return;
  const el = document.head.querySelector(selector);
  if (el) el.remove();
}

// Sync a meta tag that may hold several values (e.g. geo.region for multiple
// markets): clear every existing tag with that name, then add one per value.
// Accepts a string or a string[]; falsy entries are dropped.
function syncMultiMeta(name, value) {
  if (typeof document === "undefined") return;
  document.head.querySelectorAll(`meta[name="${name}"]`).forEach((el) => el.remove());
  const values = (Array.isArray(value) ? value : [value]).filter(Boolean);
  values.forEach((v) => {
    const el = document.createElement("meta");
    el.setAttribute("name", name);
    el.setAttribute("content", v);
    document.head.appendChild(el);
  });
}

export function applyMeta({ title, description, path, ogImage, keywords, geoRegion, geoPlacename, noIndex }) {
  if (typeof document === "undefined") return;
  const meta = buildMeta({ title, description, path, ogImage });
  document.title = meta.fullTitle;
  ensureMeta('meta[name="description"]', { tag: "meta", name: "description", content: meta.description });
  ensureMeta('link[rel="canonical"]', { tag: "link", rel: "canonical", href: meta.url });
  // Keywords ,  set when provided, otherwise clear any stale tag so it never
  // leaks onto a page that did not opt in. Accepts a string or a string[].
  const kw = Array.isArray(keywords) ? keywords.filter(Boolean).join(", ") : (keywords || "");
  if (kw) {
    ensureMeta('meta[name="keywords"]', { tag: "meta", name: "keywords", content: kw });
  } else {
    removeMeta('meta[name="keywords"]');
  }
  // Geo targeting meta. geo.region defaults to the primary markets (US, GB, IN)
  // on every indexable page; a page may override with its own value(s), and
  // noindex pages get none. geo.placename is set only when a page provides it.
  const hasRegion = Array.isArray(geoRegion) ? geoRegion.filter(Boolean).length > 0 : Boolean(geoRegion);
  const effectiveRegion = hasRegion ? geoRegion : (noIndex ? [] : GEO_REGIONS);
  syncMultiMeta("geo.region", effectiveRegion);
  syncMultiMeta("geo.placename", geoPlacename);
  // Robots ,  noindex pages opt out; every other page keeps the site default
  // (matches the static tag in index.html, including max-image-preview:large).
  ensureMeta('meta[name="robots"]', {
    tag: "meta",
    name: "robots",
    content: noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
  });
  // Open Graph
  ensureMeta('meta[property="og:title"]', { tag: "meta", property: "og:title", content: meta.fullTitle });
  ensureMeta('meta[property="og:description"]', { tag: "meta", property: "og:description", content: meta.description });
  ensureMeta('meta[property="og:url"]', { tag: "meta", property: "og:url", content: meta.url });
  ensureMeta('meta[property="og:type"]', { tag: "meta", property: "og:type", content: "website" });
  ensureMeta('meta[property="og:image"]', { tag: "meta", property: "og:image", content: meta.ogImage });
  ensureMeta('meta[property="og:site_name"]', { tag: "meta", property: "og:site_name", content: SITE_NAME });
  // Dimensions are only known for our own default image; a page-supplied
  // ogImage (e.g. a blog cover) may be a different size, so leave those tags
  // untouched/absent rather than advertise the wrong size.
  if (meta.usingDefaultImage) {
    ensureMeta('meta[property="og:image:width"]', { tag: "meta", property: "og:image:width", content: "1200" });
    ensureMeta('meta[property="og:image:height"]', { tag: "meta", property: "og:image:height", content: "630" });
    ensureMeta('meta[property="og:image:type"]', { tag: "meta", property: "og:image:type", content: "image/png" });
  } else {
    removeMeta('meta[property="og:image:width"]');
    removeMeta('meta[property="og:image:height"]');
    removeMeta('meta[property="og:image:type"]');
  }
  // Twitter
  ensureMeta('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });
  ensureMeta('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: meta.fullTitle });
  ensureMeta('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: meta.description });
  ensureMeta('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: meta.ogImage });
}

export function injectJsonLd(id, data) {
  if (typeof document === "undefined") return;
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function removeJsonLd(id) {
  if (typeof document === "undefined") return;
  const el = document.getElementById(id);
  if (el) el.remove();
}

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: DEFAULT_URL,
  logo: `${DEFAULT_URL}/brand/zarantech-logo.png`,
  description:
    "ZaranTech is a global EdTech company providing IT training in SAP, Workday, AI tools, and enterprise technologies to professionals and corporate teams.",
  sameAs: [],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: DEFAULT_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${DEFAULT_URL}/courses?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function buildCourseSchema(course) {
  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: course.title,
    description: course.description,
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      sameAs: DEFAULT_URL,
    },
    courseMode: (course.format || []).join(", "),
    educationalLevel: course.level,
  };
}

export function buildEventSchema(session, url) {
  const start = session.start_at || undefined;
  // endDate is optional per Google's own Event spec -- when we don't have a
  // real end time, omit it rather than fabricating one equal to startDate,
  // which would describe a zero-length event that "ends" the instant it
  // starts (and could read as already over almost as soon as it's crawled).
  const end = session.end_at || undefined;
  const pageUrl = url || DEFAULT_URL;
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: session.title,
    description: session.seo_description || session.subtitle || session.description || undefined,
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    eventStatus:
      session.effective_status === "cancelled"
        ? "https://schema.org/EventCancelled"
        : "https://schema.org/EventScheduled",
    startDate: start,
    endDate: end,
    image: session.og_image_url || session.banner_url || session.thumbnail_url || undefined,
    location: {
      "@type": "VirtualLocation",
      url: pageUrl,
    },
    organizer: { "@type": "Organization", name: SITE_NAME, url: DEFAULT_URL },
    performer: session.speaker?.name
      ? { "@type": "Person", name: session.speaker.name }
      : undefined,
    // All live sessions are free, registration-gated webinars -- no paid
    // tier exists for this content type yet.
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: pageUrl,
    },
  };
}

export function buildVideoSchema(session, url) {
  const vid = session?.video?.video_id;
  if (!vid) return null;
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: session.title,
    description: session.seo_description || session.subtitle || session.description || session.title,
    thumbnailUrl: session.thumbnail_url || `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`,
    uploadDate: session.start_at || session.created_at || undefined,
    contentUrl: `https://www.youtube.com/watch?v=${vid}`,
    embedUrl: `https://www.youtube.com/embed/${vid}`,
  };
}

export function buildBreadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${DEFAULT_URL}${it.path}`,
    })),
  };
}
